import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import classnames from 'classnames';
import { StatusButton } from '../UserAdd';
import { sanitizeTitle } from './Metadata';
import { compareSchema, getPublishedDiff, makeMockItem } from '../../utils/republishUtils';
import { Link } from 'react-router-dom';
import Loading from '../../components/Loading';
import { getFilePath } from '../../utils';
import { getValidationErrorsForAssets } from '../../utils/assetErrors';
import Layout from '../Layout';
import { useSelector } from 'react-redux';
import { AUDIO_STATUS } from './AdvancedAudio';
import { toast } from 'react-toastify';
import ConfirmButton from '../../components/ConfirmButton';

const fieldTitleMap = {
    description: 'Description',
    authors: 'Author(s)',
    narrators: 'Narrator(s)',
    abridgement: 'Abridgement',
    original_release_date: 'Original Release Date',
    strict_onsale_date: 'Strict On-Sale Date',
    grade_level: 'Grade Level',
    bisac_codes: 'Bisac Code(s)',
    copyright_year: 'Copyright Year',
    copyright_statement: 'Copyright Statement',
    'cover image': 'Cover Image',
    audio: 'Audio Files',
    isbn: 'Audiobook Retail ISBN',
    territories_retail: 'Territories'
};

export const submitTitle = (titleId, metadataOnly = false, forceSubmit = false) => {
    return axios
        .request({
            url: `/authorized/titles/${titleId}/submit`,
            params: {
                metadataOnly: metadataOnly ? 'True' : undefined,
                force_full_republish: forceSubmit ? 'True' : undefined
            },
            method: 'GET'
        })
        .then((resp) => resp.data);
};

const getSnapshot = (titleId) => {
    return axios
        .request({
            url: `/authorized/titles/${titleId}/snapshot`,
            method: 'GET'
        })
        .then((resp) => resp.data);
};

const ReviewSubmit = () => {
    const [success, setSuccess] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [error, setError] = useState(null);

    const isAdmin = useSelector((state) => state?.session?.isAdmin);
    const { data } = useSelector((state) => state.title);
    const submit = async () => {
        try {
            await submitTitle(data.id, !assetDiffs?.assetTypesChanged?.includes('audio'));
            setSuccess(true);
        } catch (error) {
            console.error(error);
            setError(error);
        }
    };

    useEffect(() => {
        if (!!data?.id) {
            getSnapshot(data.id).then((data) => {
                if (!Object.keys(data).length) {
                    setSnapshot(makeMockItem());
                    return;
                }
                setSnapshot(data);
            });
        }
    }, [data]);

    let hasPending = useMemo(
        () => data?.assets?.some((a) => !a.status || a.status === AUDIO_STATUS.PENDING),
        [data.assets]
    );
    let assetErrors = useMemo(() => getValidationErrorsForAssets(data?.assets), [data.assets]);
    let hasErrors = !!assetErrors.length;
    if (!snapshot) return <Loading />;

    let cleanData = sanitizeTitle(data, ['_modified']);
    let diffs = getPublishedDiff(compareSchema, cleanData, snapshot);

    let hasDiffs = !!Object.keys(diffs).length;
    let metadataDiffs = Object.keys(diffs)
        .map((k) => diffs[k])
        .filter((d) => !d.excludeFromList);
    let assetDiffs = diffs['assets'];

    let dataAudio = data?.assets?.filter((a) => a.assettype === 'audio');
    let snapAudio = snapshot?.assets?.filter((a) => a.assettype === 'audio');

    let hasRemovedAllAudioAfterPublish = !dataAudio.length && !!snapAudio.length;

    const handleRepublish = async () => {
        let tst = toast('Submitting title...', { autoClose: false });
        try {
            await submitTitle(data.id, false, true);
            toast.update(tst, { render: 'Title Submitted', type: toast.TYPE.SUCCESS, autoClose: 3000 });
        } catch (error) {
            toast.update(tst, {
                render: `Error Submitting Title`,
                type: toast.TYPE.ERROR,
                autoClose: 3000
            });
        }
    };

    return (
        <Layout>
            <div className="row">
                <div className="col-xs-12">
                    <h1 className={classnames('h4', { 'mb-5': !hasDiffs })}>Review Changes</h1>

                    {!hasDiffs && <div className="card">No Changes</div>}

                    {isAdmin && (
                        <div className="card my-4 txt-center">
                            <ConfirmButton
                                label={`Force ${data?.published ? 'Republish' : 'Publish'}`}
                                confirmLabel={`Are you sure? This will ${
                                    data?.published ? 'republish' : 'publish'
                                } the title including all assets and metadata.`}
                                successLabel="Title Submitted"
                                onConfirm={handleRepublish}
                            />
                        </div>
                    )}

                    {hasDiffs && (
                        <>
                            <p>
                                Below are any changes you've saved to the metadata, artwork, and audio files of your
                                audiobook. You must press Submit for these changes to go live with INaudio's Partner
                                network.
                            </p>
                            <div className="flash flash-light mb-5">
                                <p>
                                    Items with a green font (<ins>like this</ins>) represent the values you are
                                    submitting today. Any items with red strike-through (<del>like this</del>) represent
                                    any existing values in INaudio's system.
                                </p>
                            </div>

                            <h2 className="h5">Metadata Changes</h2>
                            <p className="mb-4">
                                These are the changes you've made since your last submit.{' '}
                                <Link to={`/catalog/${data.id}/metadata`} className="btn-link">
                                    Click Here
                                </Link>{' '}
                                if you want to make any additional changes to your Metadata
                            </p>

                            {!metadataDiffs.length && <div className="card mb-5">No Metadata Changes</div>}
                            {!!metadataDiffs.length && (
                                <div className="row mb-5">
                                    {metadataDiffs.map((diff) => (
                                        <div key={diff.name} className="col-xs-12 col-md-6 mb-3">
                                            <div className="card">
                                                <strong>{diff.name}:</strong> <ins>{diff.current || null}</ins>{' '}
                                                <del>{diff.previous || null}</del>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {assetDiffs && (
                                <div className="mb-5">
                                    <h2 className="h5">Asset Changes</h2>
                                    <p>
                                        These are the changes you've made since your last submit.{' '}
                                        <Link to={`/catalog/${data.id}/assets`} className="btn-link">
                                            Click Here
                                        </Link>{' '}
                                        if you want to make any additional changes to your audio.
                                    </p>
                                    {hasPending && (
                                        <div className="flash flash-warning mb-4">
                                            <p>
                                                Some of your assets are pending validation, this will prevent your title
                                                from being submitted.{' '}
                                                <Link to={`/catalog/${data.id}/assets`} className="btn-link">
                                                    Click Here
                                                </Link>{' '}
                                                to review and edit your audio.
                                            </p>
                                        </div>
                                    )}
                                    {hasErrors && (
                                        <div className="flash flash-error mb-4">
                                            <p>
                                                Some of your assets have validation errors, this will prevent your title
                                                from being submitted.
                                            </p>
                                            <ul className="mb-0">
                                                {assetErrors.map((assetError) => (
                                                    <li key={assetError.id}>
                                                        <strong>{assetError.label}:</strong> {assetError.reason}
                                                        {assetError.page && (
                                                            <>
                                                                {' '}
                                                                <Link
                                                                    to={`/catalog/${data.id}/${assetError.page}`}
                                                                    className="btn-link"
                                                                >
                                                                    {assetError.page === 'metadata'
                                                                        ? 'Replace your cover art'
                                                                        : 'Edit your audio'}
                                                                </Link>
                                                            </>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {hasRemovedAllAudioAfterPublish && (
                                        <div className="flash flash-error mb-4">
                                            <p>
                                                It appears you have removed all audio from this previously published
                                                project. We cannot re-publish a title if the audio has been removed.
                                                <Link to={`/catalog/${data.id}/assets`} className="btn-link">
                                                    Click here
                                                </Link>{' '}
                                                to update your audio, or{' '}
                                                <a
                                                    href="https://support.passport.inaudio.com/support/home"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    submit a ticket
                                                </a>{' '}
                                                if you would like to have this title deactivated.
                                            </p>
                                        </div>
                                    )}
                                    {assetDiffs.current.map((file, ind) => {
                                        return (
                                            <div className="card mb-3" key={ind}>
                                                <ins>{getFilePath(file)}</ins>
                                            </div>
                                        );
                                    })}
                                    {assetDiffs.previous.map((file, ind) => {
                                        return (
                                            <div className="card mb-3" key={ind}>
                                                <del>{getFilePath(file)}</del>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {!!diffs?.assetsOrderChange && (
                                <div className="mb-5">
                                    <h2 className="h5">Assets Changes</h2>
                                    <div className="card">
                                        <ins>
                                            You've made changes to the order of your audio tracks and will need to
                                            resubmit.
                                        </ins>
                                    </div>
                                </div>
                            )}

                            <div className="card text-center">
                                <div>
                                    <StatusButton
                                        label="Submit"
                                        successLabel="Title Submitted"
                                        success={success}
                                        onClick={submit}
                                        disabled={success || hasRemovedAllAudioAfterPublish || hasErrors}
                                    />
                                </div>
                                <div>
                                    {success && (
                                        <>
                                            <Link to="/catalog" className="btn-link">
                                                Go to my catalog
                                            </Link>
                                        </>
                                    )}
                                    {error && (
                                        <Link to={`/catalog/${data.id}/metadata`} className="btn-link">
                                            Correct Errors
                                        </Link>
                                    )}
                                </div>
                            </div>

                            {error && (
                                <div className="flash flash-danger mt-4">
                                    {error?.response?.data?.message && error.response.data.message}
                                    {!error?.response && 'There was an error submitting your title.'}
                                    {error?.response?.data?.missing && (
                                        <>
                                            {!!error?.response?.data?.missing?.length && (
                                                <p>
                                                    You are missing the following metadata fields.{' '}
                                                    {error?.response?.data?.missing
                                                        .filter((key) => key !== 'audio')
                                                        .map((key) => fieldTitleMap[key] || key)
                                                        .join(', ')}
                                                </p>
                                            )}
                                            {!!error?.response?.data?.missing.includes('audio') && (
                                                <p>
                                                    Audiobooks may not be submitted without corresponding{' '}
                                                    <Link to={`/catalog/${data.id}/assets`}>audio files</Link>
                                                </p>
                                            )}
                                        </>
                                    )}
                                    {error?.response?.data?.invalid && !!error?.response?.data?.invalid?.length && (
                                        <p>
                                            The following metadata fields are invalid.{' '}
                                            {error?.response?.data?.invalid
                                                .map((key) => fieldTitleMap[key] || key)
                                                .join(', ')}
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default ReviewSubmit;
